import { computed, effect, EffectRef, Injectable, Signal } from '@angular/core';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiSignalRService } from '@fi-shared/fi-signal-r/fi-signal-r.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { AppTextService } from '@fi-shared/app-text';
import { ChooseBusinessUnitDialogComponent } from '@fi-shared/fi-user-shared/choose-business-unit-dialog/choose-business-unit-dialog.component';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import { FiAuthenticateSharedService } from '@fi-shared/fi-auth-shared/fi-authenticate-shared.service';
import { RelativeDateService } from '@fi-shared/relative-date/relative-date.service';
import { ILightWeightObject } from '@fi-shared/models';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { AccessRightType, ApplicationSettingsType } from '@fi-shared/types';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';

@Injectable()
export class FiUserSharedService {
    constructor(
        private userDataService: FiUserDataService,
        private customerDataService: FiCustomerDataService,
        private fiSignalRService: FiSignalRService,
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private fiBackend: FiBackendService,
        private fiAuthenticateService: FiAuthenticateSharedService,
        private accessToApplicationTypeService: AccessToApplicationTypeService,
        private relativeDateService: RelativeDateService,
        private fiLoading: LoadingService,
    ) {}

    init() {
        this.fiSignalRService.userBusinessUnitsUpdatedEvent.subscribe((data) => {
            if (data.UserId === this.userDataService.getUserId()) {
                if (data.BusinessUnits.length) {
                    this.fiDialogsService
                        .custom({
                            headerText: this.appTextService.get('user.unit_deactivated_header'),
                            yesButtonText: this.appTextService.get('common.switch_unit'),
                            contentComponentRef: ChooseBusinessUnitDialogComponent,
                            contentComponentData: {
                                businessUnits: data.BusinessUnits,
                            },
                            config: {
                                maxWidth: '340px',
                            },
                        })
                        .onYesButtonClick((newBusinessUnit) => {
                            this.saveUserPreferredBusinessUnit(newBusinessUnit).then(() => window.location.reload());
                        })
                        .onNoButtonClick(() => {
                            this.fiAuthenticateService.logout();
                        });
                } else {
                    this.fiDialogsService
                        .notify({
                            headerText: this.appTextService.get('user.unit_deactivated_header'),
                            messageText: this.appTextService.get('user.unit_deactivated_message'),
                            yesButtonText: this.appTextService.get('user.unit_deactivated_yes_button_text'),
                            isNoButtonHidden: true,
                        })
                        .onYesButtonClick(() => {
                            window.location.reload();
                        });
                }
            }
        });
    }

    saveUserPreferredBusinessUnit(businessUnit: INameIdObj) {
        const promise = this.fiBackend.fetchDataPost(`Core/User/SaveUserPreferredBusinessUnit/${businessUnit.Id}`);
        promise.then(() => {
            this.userDataService.setPreferredBusinessUnit(businessUnit);
        });
        return this.fiLoading.trackPromise(promise);
    }

    getLatestActivity(latestActivity: string | Date, currentUserHasEditAccess: boolean): string {
        if (!currentUserHasEditAccess) {
            return '';
        }
        if (!latestActivity) {
            return this.appTextService.get('common.never');
        }

        if (typeof latestActivity === 'string') latestActivity = new Date(latestActivity);

        return this.relativeDateService.getDateForUserLatestActivity(latestActivity);
    }

    getLatestActivityColumnHiddenSignal(usersSignal: Signal<{ HasEditAccess: boolean }[]>): Signal<boolean> {
        return computed<boolean>(() => {
            const users = usersSignal();
            if (
                !this.accessToApplicationTypeService.HasAccessToApplicationType(
                    ApplicationSettingsType.CoreLocal,
                    AccessRightType.Write,
                )
            ) {
                return true;
            }

            const isLatestActivityColumnHidden = !users.find((u) => u.HasEditAccess);
            return isLatestActivityColumnHidden;
        });
    }

    getDisablePushNotificationsUntilDate(): Promise<string> {
        const promise = this.fiBackend.fetchData<string>(`api2/Common/GetDisablePushNotificationsUntilDate`);
        return this.fiLoading.trackPromise(promise);
    }

    setDisablePushNotificationsUntilDate(untilDate: string): Promise<void> {
        const promise = this.fiBackend.fetchDataPost<void>(
            `api2/Common/SetDisablePushNotificationsUntilDate?untilDate=${untilDate ? untilDate : ''}`,
        );
        return this.fiLoading.trackPromise(promise);
    }
}
